import React from "react";
import { CheckCircle2, ExternalLink, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

/* Programmatic anchor clicks open every leg reliably; repeated
 * window.open() calls get pop-up-blocked after the first. */
function openAllChunks(chunks) {
  let blocked = 0;
  chunks.forEach((c) => {
    try {
      const a = document.createElement("a");
      a.href = c.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (_) {
      blocked += 1;
    }
  });
  if (blocked > 0) {
    toast.error(
      blocked === chunks.length
        ? "Your browser blocked the new tabs. Please allow pop-ups for this site."
        : `${blocked} tab${blocked === 1 ? " was" : "s were"} blocked by your browser. Allow pop-ups to open every leg at once.`,
    );
  }
}

export default function RouteResult({ result, testIdPrefix: p, children }) {
  if (!result) return null;
  const multi = result.chunks.length > 1;
  const unmapped = Array.isArray(result.unmapped) ? result.unmapped : [];
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3" data-testid={`${p}-result`}>
      <div className="text-sm font-medium text-emerald-900 flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4" />
        Route built · {result.chunk_count} URL{result.chunk_count === 1 ? "" : "s"} · {result.total_stops} stops
      </div>
      {unmapped.length > 0 ? (
        <div
          className="mt-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid={`${p}-unmapped-warning`}
        >
          <div className="font-semibold flex items-center gap-1.5">
            <AlertTriangle className="h-3.5 w-3.5" />
            {unmapped.length} stop{unmapped.length === 1 ? "" : "s"} couldn't be placed on the map
          </div>
          <p className="mt-1 leading-snug">
            These have no map coordinates (the address is likely too new for Google Maps), so Google may
            not find them and they won't be pinned accurately. Everything else is routed by exact coordinates.
          </p>
          <ul className="mt-1 list-disc list-inside space-y-0.5">
            {unmapped.slice(0, 6).map((a, i) => (
              <li key={i} className="truncate">{a}</li>
            ))}
            {unmapped.length > 6 ? <li>…and {unmapped.length - 6} more</li> : null}
          </ul>
        </div>
      ) : null}
      <div className="mt-2 space-y-2">
        {result.chunks.map((c, idx) => (
          <a
            key={idx}
            href={c.url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center justify-between gap-2 text-sm text-slate-900 bg-white border border-slate-200 rounded px-3 py-2 hover:bg-slate-50 min-h-[44px]"
            data-testid={`${p}-chunk-link-${idx}`}
          >
            <span>
              {multi
                ? `Leg ${idx + 1} · ${c.stop_count} stop${c.stop_count === 1 ? "" : "s"}`
                : `${c.stop_count} stop${c.stop_count === 1 ? "" : "s"} in Google Maps`}
            </span>
            <span className="text-sm font-medium whitespace-nowrap" style={{ color: "hsl(var(--brand))" }}>
              {multi ? `Open Leg ${idx + 1}` : "Open"}
            </span>
          </a>
        ))}
      </div>
      {multi ? (
        <>
          <Button
            onClick={() => openAllChunks(result.chunks)}
            className="mt-2 text-white min-h-[44px] w-full"
            style={{ background: "hsl(var(--brand))" }}
            data-testid={`${p}-open-all-button`}
          >
            <ExternalLink className="h-4 w-4 mr-2" /> Open all legs in new tabs
          </Button>
          <p className="text-xs text-slate-500 leading-snug mt-1" data-testid={`${p}-open-all-hint`}>
            Only 1 tab opened? Your browser is blocking pop-ups. Click the pop-up icon in the address bar and
            choose "Always allow", or open each leg above individually.
          </p>
        </>
      ) : null}
      {children}
    </div>
  );
}
